import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Autocomplete,
  TextField,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Person, LocationOn, Business, TrendingUp, Schedule } from '@mui/icons-material';
import { DirectReport } from '../types';
import { api } from '../services/api';
import { useAuth } from 'react-oidc-context';

interface DirectReportsFilterProps {
  selectedReport: DirectReport | null;
  onReportChange: (report: DirectReport | null) => void;
}

const DirectReportsFilter: React.FC<DirectReportsFilterProps> = ({ selectedReport, onReportChange }) => {
  const [directReports, setDirectReports] = useState<DirectReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  // Get manager alias from OIDC profile or use sttanksa for local dev
  const cognitoConfigured = process.env.REACT_APP_COGNITO_ISSUER && process.env.REACT_APP_COGNITO_CLIENT_ID;
  const isLocalDev = !cognitoConfigured && process.env.NODE_ENV === 'development';

  const managerAlias = isLocalDev
    ? 'sttanksa' // Always use sttanksa for manager queries in local dev
    : auth.user?.profile?.preferred_username || auth.user?.profile?.email?.split('@')[0] || 'unknown-user';

  useEffect(() => {
    if (managerAlias && managerAlias !== 'unknown-user') {
      fetchDirectReports();
    }
  }, [managerAlias]);

  const fetchDirectReports = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call PAPI to get real direct reports data
      const response = await api.getDirectReports();

      console.log('Direct reports response:', response);

      if (response.directReports && response.directReports.length > 0) {
        // Sort direct reports alphabetically by name
        const sortedReports = response.directReports.sort((a: DirectReport, b: DirectReport) =>
          a.name.localeCompare(b.name),
        );
        setDirectReports(sortedReports);
      } else {
        // No direct reports found
        setDirectReports([]);
        setError('No direct reports found for this manager.');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching direct reports:', error);
      setError('Failed to load direct reports. Please try again.');

      // Fall back to mock data for development (matches backend mock data)
      const mockReports: DirectReport[] = [
        {
          alias: 'astankey',
          name: 'Alex Stankey',
          region: 'North America',
          level: 'L4',
          location: 'Arlington, VA',
          role: 'Solutions Architect',
          tenure: '2y 3m',
          photoUrl: 'https://via.placeholder.com/40x40/4285f4/ffffff?text=AS',
        },
        {
          alias: 'jsmith',
          name: 'John Smith',
          region: 'North America',
          level: 'L5',
          location: 'Seattle, WA',
          role: 'Senior Solutions Architect',
          tenure: '3y 1m',
          photoUrl: 'https://via.placeholder.com/40x40/34a853/ffffff?text=JS',
        },
        {
          alias: 'mwilson',
          name: 'Maria Wilson',
          region: 'North America',
          level: 'L4',
          location: 'Austin, TX',
          role: 'Solutions Architect',
          tenure: '1y 8m',
          photoUrl: 'https://via.placeholder.com/40x40/9c27b0/ffffff?text=MW',
        },
      ];

      // Sort mock reports alphabetically by name as well
      const sortedMockReports = mockReports.sort((a: DirectReport, b: DirectReport) => a.name.localeCompare(b.name));
      setDirectReports(sortedMockReports);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" gutterBottom>
          Select direct report
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading direct reports for {isLocalDev ? 'Sameer Tanksale (sttanksa)' : managerAlias}...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h2" gutterBottom>
        Select direct report
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box mb={3}>
        <Autocomplete
          options={directReports}
          getOptionLabel={(option) => `${option.name} (${option.alias})`}
          value={selectedReport}
          onChange={(event, newValue) => onReportChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Direct Report"
              placeholder="Choose a team member to filter data..."
              variant="outlined"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={option.photoUrl} alt={option.name} sx={{ width: 32, height: 32 }}>
                {option.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {option.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.alias} • {option.role}
                </Typography>
              </Box>
            </Box>
          )}
          clearOnEscape
          clearText="Clear selection"
        />
      </Box>

      {selectedReport && (
        <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar src={selectedReport.photoUrl} alt={selectedReport.name} sx={{ width: 56, height: 56 }}>
                {selectedReport.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedReport.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedReport.alias}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Business fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Role
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedReport.role}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUp fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Level
                    </Typography>
                    <Chip
                      label={selectedReport.level}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedReport.location}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Person fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Region
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedReport.region}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Schedule fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Tenure
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedReport.tenure}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {!selectedReport && (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="text.secondary">
            Select a direct report to filter all data entry grids below
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DirectReportsFilter;
